import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ReviewForm from '../components/reviews/ReviewForm';
import { cloutReviewsApi, InviteLookup, ReviewInput } from '../services/cloutReviews';
import { getApiErrorMessage } from '../utils/errors';
import cloutLogo from '../assets/logos/clout-logo.png';

type State =
  | { step: 'loading' }
  | { step: 'invalid'; message: string }
  | { step: 'writing'; invite: InviteLookup }
  | { step: 'done'; message: string };

// Where a provider's review invite email lands: /write-review?invite=<token>.
// Opening the link changes nothing (mail scanners open links too); only submitting uses it.
const WriteReview: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('invite');
  const [state, setState] = useState<State>({ step: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ step: 'invalid', message: "This review link isn't valid." });
      return;
    }
    let cancelled = false;
    cloutReviewsApi
      .lookUpInvite(token)
      .then((invite) => !cancelled && setState({ step: 'writing', invite }))
      .catch(
        (err) =>
          !cancelled &&
          setState({
            step: 'invalid',
            message: getApiErrorMessage(err, "This review link isn't valid."),
          })
      );
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (review: ReviewInput) => {
    if (!token) return;
    const result = await cloutReviewsApi.submitWithInvite(token, review);
    setState({ step: 'done', message: result.message });
  };

  return (
    <div className="min-h-screen bg-[#fcfaf8]">
      <header className="bg-[#f8f6f2]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex justify-center">
          <Link to="/">
            <img src={cloutLogo} alt="CLOUT Aesthetics" className="h-10 sm:h-12" />
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-8">
          {state.step === 'loading' && <p className="text-[#767472]">Loading…</p>}

          {state.step === 'invalid' && (
            <>
              <h1 className="font-['Ovo'] text-2xl text-[#1f1f1f] mb-3">Review link unavailable</h1>
              <p role="alert" className="font-['Open_Sans'] text-[#767472]">
                {state.message}
              </p>
            </>
          )}

          {state.step === 'writing' && (
            <>
              <h1 className="font-['Ovo'] text-2xl text-[#1f1f1f] mb-2">
                Review {state.invite.provider_name}
              </h1>
              <p className="font-['Open_Sans'] text-sm text-[#767472] mb-6">
                {state.invite.provider_name} invited you to share your experience on CLOUT. This
                link works once.
              </p>
              <ReviewForm providerName={state.invite.provider_name} onSubmit={handleSubmit} />
            </>
          )}

          {state.step === 'done' && (
            <>
              <h1 className="font-['Ovo'] text-2xl text-[#1f1f1f] mb-3">Thank you</h1>
              <p role="status" className="font-['Open_Sans'] text-[#1f1f1f]">
                {state.message}
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default WriteReview;
